import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, ArrowUpRight, Calendar, Tag, Briefcase, Building2, type LucideIcon } from 'lucide-react';
import * as Icons from 'lucide-react';
import { Meta } from '@/components/seo/Meta';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardDescription, CardHeader } from '@/components/ui/Card';
import { Separator } from '@/components/ui/Separator';
import { FadeIn, Stagger, StaggerItem } from '@/components/motion/FadeIn';
import { Skeleton } from '@/components/ui/Skeleton';
import { GalleryGrid } from '@/components/ui/Lightbox';
import { MarkdownRenderer } from '@/components/marketing/MarkdownRenderer';
import { useProjectBySlug, useProjects } from '@/lib/cms';

export function ProjectDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const { data: project, isLoading, isError } = useProjectBySlug(slug);
  const { data: allProjects = [] } = useProjects();
  const currentIndex = allProjects.findIndex((p) => p.slug === slug);
  const nextProject = currentIndex >= 0 ? allProjects[(currentIndex + 1) % allProjects.length] : undefined;

  if (isLoading) {
    return (
      <>
        <Meta title="Loading…" path={`/projects/${slug ?? ''}`} />
        <div className="pt-24 sm:pt-28 md:pt-32">
          <Skeleton className="h-[50vh] w-full" />
        </div>
      </>
    );
  }

  if (isError || !project) {
    return (
      <>
        <Meta title="Project not found" path={`/projects/${slug ?? ''}`} />
        <section className="flex min-h-[70vh] items-center justify-center pt-20">
          <div className="container-narrow text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold-500">404</p>
            <h1 className="mt-3 display-serif text-display-l text-fg">We couldn't find that project.</h1>
            <p className="mt-3 text-fg-muted">It may have been unpublished or moved.</p>
            <div className="mt-6">
              <Button asChild>
                <Link to="/projects">
                  <ArrowLeft className="h-4 w-4" /> All projects
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </>
    );
  }

  const lightboxImages = [
    ...(project.hero_image_url
      ? [{ src: project.hero_image_url, alt: `${project.title} hero image` }]
      : []),
    ...project.gallery.map((g) => ({ src: g.url, alt: g.alt })),
  ];

  return (
    <>
      <Meta
        title={project.title}
        description={project.summary ?? undefined}
        path={`/projects/${project.slug}`}
        image={project.hero_image_url ?? undefined}
        jsonLd={
          {
            '@context': 'https://schema.org',
            '@type': 'CreativeWork',
            name: project.title,
            description: project.summary ?? undefined,
            keywords: project.tech_stack.join(', '),
            url: `https://vonlof.co/projects/${project.slug}`,
            provider: { '@type': 'Organization', name: 'Vonlof', url: 'https://vonlof.co' },
            datePublished: project.published_at ?? undefined,
          } as never
        }
      />
      <main>
        {/* HERO */}
        <section className="relative isolate pt-32 pb-16">
          <div aria-hidden className="absolute inset-0 -z-10 gradient-hero" />
          <div className="container-wide">
            <FadeIn>
              <Link
                to="/projects"
                className="inline-flex items-center gap-1 text-sm text-fg-muted transition-colors hover:text-gold-500"
              >
                <ArrowLeft className="h-3.5 w-3.5" /> All projects
              </Link>
            </FadeIn>
            <FadeIn delay={0.05}>
              <div className="mt-6 flex flex-wrap items-center gap-2">
                <Badge variant="gold">{project.category}</Badge>
                <Badge>{labelForStatus(project.status)}</Badge>
                {project.year && <Badge variant="outline">{project.year}</Badge>}
                {project.client && <Badge variant="outline">{project.client}</Badge>}
              </div>
            </FadeIn>
            <FadeIn delay={0.1}>
              <h1 className="mt-6 display-serif text-display-l text-fg">{project.title}</h1>
            </FadeIn>
            {project.summary && (
              <FadeIn delay={0.15}>
                <p className="mt-6 max-w-2xl text-lg text-fg-muted">{project.summary}</p>
              </FadeIn>
            )}
          </div>
        </section>

        {/* HERO IMAGE */}
        {project.hero_image_url && (
          <section className="pb-16">
            <div className="container-wide">
              <FadeIn>
                <div className="aspect-[16/9] overflow-hidden rounded-xl border border-border bg-bg-subtle">
                  <img
                    src={project.hero_image_url}
                    alt={`${project.title} hero image`}
                    loading="eager"
                    decoding="async"
                    fetchPriority="high"
                    className="h-full w-full object-cover"
                  />
                </div>
              </FadeIn>
            </div>
          </section>
        )}

        {/* META + IMPACT */}
        {(project.problem || project.approach || project.business_impact.length > 0) && (
          <section className="pb-16">
            <div className="container-wide grid gap-12 lg:grid-cols-3">
              <FadeIn className="lg:col-span-2 space-y-12">
                {project.problem && (
                  <div>
                    <SectionHeading eyebrow="Problem" headline="What we set out to solve." />
                    <MarkdownRenderer content={project.problem} />
                  </div>
                )}
                {project.approach && (
                  <div>
                    <SectionHeading eyebrow="Approach" headline="How we tackled it." />
                    <MarkdownRenderer content={project.approach} />
                  </div>
                )}
                {project.description_md && (
                  <div>
                    <SectionHeading eyebrow="Story" headline="The full case study." />
                    <MarkdownRenderer content={project.description_md} />
                  </div>
                )}
              </FadeIn>

              <FadeIn delay={0.1} className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardDescription>Project facts</CardDescription>
                    <div className="mt-3 space-y-3 text-sm">
                      {project.client && (
                        <Row icon={Building2} label="Client" value={project.client} />
                      )}
                      {project.year && (
                        <Row icon={Calendar} label="Year" value={String(project.year)} />
                      )}
                      <Row icon={Tag} label="Category" value={project.category} />
                      <Row icon={Briefcase} label="Status" value={labelForStatus(project.status)} />
                    </div>
                  </CardHeader>
                </Card>

                {project.tech_stack.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardDescription>Tech stack</CardDescription>
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        {project.tech_stack.map((t) => (
                          <Badge key={t} variant="outline">{t}</Badge>
                        ))}
                      </div>
                    </CardHeader>
                  </Card>
                )}

                {project.business_impact.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardDescription>Business impact</CardDescription>
                      <Stagger className="mt-3 space-y-3">
                        {project.business_impact.map((m, idx) => (
                          <StaggerItem key={`${m.label}-${idx}`}>
                            <div className="rounded-md border border-border bg-bg-subtle/40 p-3">
                              <p className="display-serif text-2xl text-gold-500">{m.value}</p>
                              <p className="text-sm font-medium">{m.label}</p>
                              {m.sublabel && <p className="text-xs text-fg-subtle">{m.sublabel}</p>}
                            </div>
                          </StaggerItem>
                        ))}
                      </Stagger>
                    </CardHeader>
                  </Card>
                )}

                {(project.cta_label || project.cta_href) && (
                  <Button asChild size="lg" className="w-full">
                    <a
                      href={project.cta_href ?? '/contact'}
                      target={project.cta_href?.startsWith('http') ? '_blank' : undefined}
                      rel={project.cta_href?.startsWith('http') ? 'noopener noreferrer' : undefined}
                    >
                      {project.cta_label ?? 'Get in touch'} <ArrowUpRight className="h-4 w-4" />
                    </a>
                  </Button>
                )}
              </FadeIn>
            </div>
          </section>
        )}

        {/* FEATURES */}
        {project.features.length > 0 && (
          <section className="pb-16">
            <div className="container-wide">
              <SectionHeading eyebrow="Features delivered" headline="What we built." />
              <Stagger className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {project.features.map((f) => {
                  const IconName = (f.icon ?? 'CheckCircle2') as keyof typeof Icons;
                  const Icon = (Icons[IconName] ?? Icons.CheckCircle2) as LucideIcon;
                  return (
                    <StaggerItem key={f.id}>
                      <div className="rounded-xl border border-border bg-bg-elevated/40 p-5">
                        <div className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-gold-500/10 text-gold-500">
                          <Icon className="h-4 w-4" />
                        </div>
                        <h3 className="mt-3 text-base font-semibold tracking-tight">{f.title}</h3>
                        {f.description && (
                          <p className="mt-1 text-sm text-fg-muted">{f.description}</p>
                        )}
                      </div>
                    </StaggerItem>
                  );
                })}
              </Stagger>
            </div>
          </section>
        )}

        {/* GALLERY */}
        {project.gallery.length > 0 && (
          <section className="pb-16">
            <div className="container-wide">
              <SectionHeading eyebrow="Gallery" headline="Screenshots & visuals." />
              <div className="mt-8">
                <GalleryGrid images={lightboxImages} />
              </div>
            </div>
          </section>
        )}

        {/* NEXT PROJECT */}
        {nextProject && nextProject.slug !== project.slug && (
          <section className="pb-24">
            <div className="container-default">
              <Separator className="mb-12" />
              <Link to={`/projects/${nextProject.slug}`} className="group block">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold-500">Next project</p>
                <div className="mt-3 flex items-end justify-between gap-6">
                  <h2 className="display-serif text-3xl text-fg transition-colors group-hover:text-gold-500">
                    {nextProject.title}
                  </h2>
                  <ArrowRight className="h-6 w-6 text-gold-500 transition-transform group-hover:translate-x-1" />
                </div>
                {nextProject.summary && (
                  <p className="mt-2 max-w-2xl text-fg-muted">{nextProject.summary}</p>
                )}
              </Link>
            </div>
          </section>
        )}
      </main>
    </>
  );
}

function SectionHeading({ eyebrow, headline }: { eyebrow: string; headline: string }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold-500">{eyebrow}</p>
      <h2 className="mt-3 text-h2 text-fg">{headline}</h2>
    </div>
  );
}

function Row({ icon: Icon, label, value }: { icon: LucideIcon; label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="inline-flex items-center gap-2 text-fg-subtle">
        <Icon className="h-3.5 w-3.5" /> {label}
      </span>
      <span className="text-fg">{value}</span>
    </div>
  );
}

function labelForStatus(status: 'shipped' | 'ongoing' | 'concept'): string {
  switch (status) {
    case 'shipped':
      return 'Shipped';
    case 'ongoing':
      return 'Ongoing';
    case 'concept':
      return 'Concept';
  }
}